import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import {
  Phone,
  Email,
  Close,
  WhatsApp,
  ContentCopy
} from '@mui/icons-material';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  contactData: {
    name: string;
    email?: string;
    phone?: string;
    type: 'artist' | 'venue' | 'technical';
  };
}

const ContactModal: React.FC<ContactModalProps> = ({
  open,
  onClose,
  contactData
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');

  const handleEmailClick = () => {
    if (contactData.email) {
      window.location.href = `mailto:${contactData.email}`;
    }
  };

  const handlePhoneClick = () => {
    if (contactData.phone) {
      window.location.href = `tel:${contactData.phone}`;
    }
  };

  const handleWhatsAppClick = () => {
    if (contactData.phone) {
      // Remove any non-numeric characters from phone number
      const cleanPhone = contactData.phone.replace(/\D/g, '');
      window.open(`https://wa.me/${cleanPhone}`, '_blank');
    }
  };

  const handleCopyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      setSnackbarOpen(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedText(type);
        setSnackbarOpen(true);
      } catch (fallbackErr) {
        console.error('Failed to copy text: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Contact {contactData.type === 'technical'
                ? 'Technical Contact'
                : contactData.type === 'artist'
                  ? 'Artist'
                  : 'Venue'}: {contactData.name}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {contactData.email && (
              <>
                <ListItem>
                  <ListItemIcon>
                    <Email color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email"
                    secondary={contactData.email}
                  />
                  <Box display="flex" gap={1}>
                    <IconButton 
                      onClick={handleEmailClick}
                      color="primary"
                      title="Open in email client"
                    >
                      <Email />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleCopyToClipboard(contactData.email!, 'email address')}
                      color="secondary"
                      title="Copy email address"
                    >
                      <ContentCopy />
                    </IconButton>
                  </Box>
                </ListItem>
                {contactData.phone && <Divider />}
              </>
            )}
            
            {contactData.phone && (
              <ListItem>
                <ListItemIcon>
                  <Phone color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Phone"
                  secondary={contactData.phone}
                />
                <Box display="flex" gap={1}>
                  <IconButton 
                    onClick={handlePhoneClick}
                    color="primary"
                    title="Call phone number"
                  >
                    <Phone />
                  </IconButton>
                  <IconButton 
                    onClick={handleWhatsAppClick}
                    color="success"
                    title="Open in WhatsApp"
                  >
                    <WhatsApp />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleCopyToClipboard(contactData.phone!, 'phone number')}
                    color="secondary"
                    title="Copy phone number"
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
              </ListItem>
            )}
            
            {!contactData.email && !contactData.phone && (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No contact information available for this {contactData.type}.
              </Typography>
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {copiedText} copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactModal;